/*
 * ===============================================================================================================
 *                                Copyright 2021, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Box, Button, Grid, TextField } from '@mui/material';

import { useStyles } from './styles';
import { MESSAGES, LABELS } from './constants';
import RegexTextInput from '../../utility/RegexTextInput';
import interpolateString from '../../utility/stringInterpolation';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { createUserGroupRequest, updateUserGroupRequest } from '../../services/userGroups';

interface IGeneralTab {
  edit: boolean;
  groupData: any;
  goToAssignUsersTab: (identifier: any) => void;
};

const formErrorDefaultValues = {
  errorDisplayName: false,
  errorIdentifier: false,
  errorDescription: false,
  errorHelperTextIdentifier: '',
  errorHelperTextDisplayName: '',
  errorHelperTextDescription: '',
};

const AddUserGroupGeneralTab = ({ edit, groupData, goToAssignUsersTab }: IGeneralTab) => {
  const { classes } = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const [displayName, setDispalyName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [description, setDescription] = useState('');
  const [formErrorState, updateFormError] = useState(formErrorDefaultValues);

  useEffect(() => {
    if (edit && groupData) {
      setDispalyName(groupData.displayName);
      setIdentifier(groupData.identifier);
      setDescription(groupData.description);
    }
  }, [edit, groupData]);

  const updateFormFields = (fieldName: string, value: any) => {
    if (fieldName === 'displayName') {
      setDispalyName(value);
      updateFormError({
        ...formErrorState,
        errorDisplayName: false,
        errorHelperTextDisplayName: '',
      });
      if (!edit) {
        const identifierAutoValue = value.replace(/ /g, '-').toLowerCase();
        setIdentifier(identifierAutoValue);
      }
    }
    if (fieldName === 'identifier') {
      setIdentifier(value);
      updateFormError({
        ...formErrorState,
        errorIdentifier: false,
        errorHelperTextIdentifier: '',
      });
    }
    if (fieldName === 'description') {
      setDescription(value);
      updateFormError({
        ...formErrorState,
        errorDescription: false,
        errorHelperTextDescription: '',
      });
    }
  };

  const validateFields = () => {
    if (!displayName || displayName.trim().length < 2) {
      updateFormError({
        ...formErrorState,
        errorDisplayName: true,
        errorHelperTextDisplayName: MESSAGES.DISPLAY_NAME_REQUIRED,
      });
      return false;
    }
    if (!identifier || identifier.trim().length < 2) {
      updateFormError({
        ...formErrorState,
        errorIdentifier: true,
        errorHelperTextIdentifier: MESSAGES.IDENTIFIER_REQUIRED,
      });
      return false;
    }
    if (!description || description.trim().length < 2) {
      updateFormError({
        ...formErrorState,
        errorDescription: true,
        errorHelperTextDescription: MESSAGES.DESCRIPTION_REQUIRED,
      });
      return false;
    }
    return true;
  };

  const onSaveUserGroup = async () => {
    if (validateFields()) {
      const userGroupData = { displayName, identifier: identifier, description };
      try {
        if (edit) {
          await updateUserGroupRequest(userGroupData);
        } else {
          await createUserGroupRequest(userGroupData);
          goToAssignUsersTab(userGroupData);
        }
        dispatch(setSnackBarMessage({
          severity: 'success',
          showSnackbar: true,
          autoHideDuration: 5000,
          message: interpolateString([`${displayName}`], edit ? MESSAGES.USER_GROUP_UPDATED : MESSAGES.USER_GROUP_CREATED),
        }));
      }
      catch (error: any) {
        dispatch(setSnackBarMessage({
          severity: 'error',
          showSnackbar: true,
          autoHideDuration: 5000,
          message: interpolateString([error?.response?.data?.message], MESSAGES.USER_GROUP_CREATE_FAILED)
        }));
      }
    }
  };

  return (
    <Box className={classes.root}>
      <Grid item xs={3} className={classes.contentContainer}>
        <RegexTextInput
          fullWidth
          required
          id="displayName"
          name="displayName"
          value={displayName}
          label={LABELS.DISPLAY_NAME}
          data-testid={'displayName'}
          regex={/[^a-zA-Z0-9 _-]+$/g}
          className={classes.textInput}
          error={formErrorState.errorDisplayName}
          inputProps={{ maxLength: 128, required: true }}
          helperText={formErrorState.errorHelperTextDisplayName}
          onChange={(e: any) => updateFormFields('displayName', e.target.value)}
        />
      </Grid>

      <Grid item xs={3} className={classes.contentContainer}>
        <RegexTextInput
          fullWidth
          required
          id="identifier"
          value={identifier}
          disabled={edit}
          name="identifier"
          label={LABELS.IDENTIFIER}
          data-testid={'identifier'}
          regex={/[^a-zA-Z0-9_-]+$/g}
          className={classes.textInput}
          error={formErrorState.errorIdentifier}
          inputProps={{ maxLength: 128, required: true }}
          helperText={formErrorState.errorHelperTextIdentifier}
          onChange={(e: any) => updateFormFields('identifier', e.target.value)}
        />
      </Grid>

      <Grid item xs={3} className={classes.contentContainer}>
        <TextField
          fullWidth
          required
          id="description"
          variant="standard"
          value={description}
          name="description"
          label={LABELS.DESCRIPTION}
          data-testid={'description'}
          className={classes.textInput}
          error={formErrorState.errorDescription}
          inputProps={{ maxLength: 128, required: true }}
          helperText={formErrorState.errorHelperTextDescription}
          onChange={(e: any) => updateFormFields('description', e.target.value)}
        />
      </Grid>
      <Box className={classes.footerWrap}>
        <Button
          size="medium"
          color="primary"
          variant="outlined"
          data-testid="cancel-btn"
          onClick={() => history.goBack()}
        >
          {LABELS.CANCEL}
        </Button>
        <Button
          size="medium"
          color="primary"
          variant="contained"
          data-testid="save-btn"
          onClick={onSaveUserGroup}
          style={{ marginLeft: '20px' }}
        >
          {LABELS.NEXT}
        </Button>
      </Box>
    </Box>
  );
};

export default AddUserGroupGeneralTab;
